/**
 * Typed client for player profiles.
 *
 * A profile only exists once an officer has linked a site account to a roster
 * entry, so most of this is either "read someone's profile" or "edit the one
 * that belongs to me".
 */

import { requestJson, type RequestOptions } from "./httpClient";
import { MEMBER_CSRF_COOKIE } from "./accountsApi";
import type { PieceType } from "@/game/pieces";
import type { ApiMeta, Player } from "./api";

export interface SocialLink {
  readonly label: string;
  readonly url: string;
}

export interface PlayerProfile {
  readonly roster_username: string;
  /** The site account behind the profile. */
  readonly account: string;
  readonly bio: string;
  readonly pronouns: string;
  readonly favourite_piece: PieceType | null;
  readonly socials: readonly SocialLink[];
  readonly has_avatar: boolean;
}

export interface ProfileRules {
  readonly max_bio_length: number;
  readonly max_pronouns_length: number;
  readonly max_social_links: number;
  readonly max_avatar_bytes: number;
  readonly pieces: readonly PieceType[];
}

export interface PlayerState {
  /** False until an officer links this account to a roster entry. */
  readonly assigned: boolean;
  readonly profile: PlayerProfile | null;
  readonly rules: ProfileRules;
}

export interface ProfileDraft {
  readonly bio: string;
  readonly pronouns: string;
  readonly favourite_piece: PieceType | null;
  readonly socials: readonly SocialLink[];
}

/** Where a player's picture is served from. */
export function avatarUrl(rosterUsername: string): string {
  return `/api/players/${encodeURIComponent(rosterUsername)}/avatar`;
}

export interface PlayerPage {
  /** The roster row: standings, rank, role. Present for every roster player. */
  readonly player: Player;
  /** Only once an officer has assigned an account and the member has written. */
  readonly profile: PlayerProfile | null;
  readonly meta: ApiMeta;
}

export const playersApi = {
  read: (rosterUsername: string, signal?: AbortSignal) =>
    requestJson<PlayerPage>(
      "/api/players",
      `/${encodeURIComponent(rosterUsername)}`,
      { signal },
      null,
    ),

  mine: (signal?: AbortSignal) =>
    requestJson<PlayerState>(
      "/api/accounts",
      "/player",
      { signal },
      MEMBER_CSRF_COOKIE,
    ),

  save: (draft: ProfileDraft) =>
    requestJson<PlayerProfile>(
      "/api/accounts",
      "/player",
      { method: "PUT", body: draft } satisfies RequestOptions,
      MEMBER_CSRF_COOKIE,
    ),

  removeAvatar: () =>
    requestJson<PlayerProfile>(
      "/api/accounts",
      "/player/avatar",
      { method: "DELETE" },
      MEMBER_CSRF_COOKIE,
    ),
};

/**
 * Upload a picture.
 *
 * `requestJson` passes a FormData body through untouched so the browser can set
 * its own multipart boundary. This used to be a hand-rolled `fetch` for that one
 * reason, and the copy had drifted: it reported a proxy's HTML error page as
 * unreadable instead of surfacing the status, which is the case the editor's
 * oversize-picture message depends on.
 */
export async function uploadAvatar(file: File): Promise<PlayerProfile> {
  const body = new FormData();
  body.append("file", file);
  return requestJson<PlayerProfile>(
    "/api/accounts",
    "/player/avatar",
    { method: "POST", body },
    MEMBER_CSRF_COOKIE,
  );
}
